import { ThunderboltOutlined } from '@ant-design/icons';
import { Badge } from 'antd';
import { useMemo, useState } from 'react';

import { Popover } from 'components/ui';
import type { PictureInferenceFilterOptionGroup } from 'horizon/routes/Inspections/InspectionDetail/pictureInferenceFilter.utils';

import { StyledButton } from '../ImageViewer.style';

import { Content, Group, GroupLabel, OptionLabel, OptionText } from './InferenceFilterTool.style';

type InferenceFilterToolProps = {
  disabled?: boolean;
  onChange: (data: { selectedInferenceDetectionTypeIds: string[] }) => void;
  options: PictureInferenceFilterOptionGroup[];
  value?: string[];
};

export const InferenceFilterTool = ({
  disabled = false,
  onChange,
  options,
  value = [],
}: InferenceFilterToolProps) => {
  const [open, setOpen] = useState(false);
  const selectedIds = useMemo(() => new Set(value), [value]);

  const handleOptionChange = (optionValue: string) => {
    const nextSelectedIds = selectedIds.has(optionValue)
      ? value.filter(id => id !== optionValue)
      : [...value, optionValue];
    onChange({ selectedInferenceDetectionTypeIds: nextSelectedIds });
  };

  const content = (
    <Content>
      {options.map(group => (
        <Group key={group.inferenceApplication}>
          <GroupLabel>{group.inferenceApplication}</GroupLabel>
          {group.options.map(option => (
            <OptionLabel key={option.value} title={option.description}>
              <input
                type="checkbox"
                checked={selectedIds.has(option.value)}
                onChange={() => handleOptionChange(option.value)}
              />
              <OptionText>{option.label}</OptionText>
            </OptionLabel>
          ))}
        </Group>
      ))}
    </Content>
  );

  return (
    <Popover
      content={content}
      open={open}
      placement="leftTop"
      trigger="click"
      onOpenChange={setOpen}
      arrow={false}
    >
      <StyledButton
        data-testid="image-controls-inference-filter"
        title="AI filters"
        type={value.length > 0 ? 'primary' : 'default'}
        disabled={disabled}
      >
        <Badge count={value.length} size="small" offset={[8, -4]}>
          <ThunderboltOutlined />
        </Badge>
      </StyledButton>
    </Popover>
  );
};
